"use client";

import { useState, useEffect, useId, ReactNode, MouseEvent } from "react";
import { createPortal } from "react-dom";

type ExternalLinkProps = {
    href: string;
    children: ReactNode;
    className?: string;
    ariaLabel?: string;
    target?: "_blank" | "_self";
    rel?: string;
};

export default function ExternalLink({
    href,
    children,
    rel,
    className,
    ariaLabel,
    target = "_blank",
}: ExternalLinkProps) {
    const [open, setOpen] = useState(false);
    const [pendingHref, setPendingHref] = useState<string | null>(null);
    const [mounted, setMounted] = useState(false);
    const uid = useId();
    const titleId = `${uid}-leaving-title`;
    const descId = `${uid}-leaving-desc`;

    useEffect(() => setMounted(true), []);

    function handleClick(e: MouseEvent<HTMLAnchorElement>) {
        if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        setPendingHref(href);
        setOpen(true);
    }

    function proceed() {
        if (!pendingHref) return;
        if (target === "_blank") {
            window.open(pendingHref, "_blank", "noopener,noreferrer");
        } else {
            window.location.assign(pendingHref);
        }
        setOpen(false);
        setPendingHref(null);
    }

    function cancel() {
        setOpen(false);
        setPendingHref(null);
    }

    const modal = open ? (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            aria-describedby={descId}
            onKeyDown={(e) => {
                if (e.key === "Escape") cancel();
            }}
        >
            <div className="w-[92%] max-w-md rounded-2xl bg-white p-6 text-gray-900 shadow-xl">
                <h2 id={titleId} className="text-lg font-semibold">
                    You’re leaving the Clearpath Lending website
                </h2>

                <p id={descId} className="mt-3 text-sm leading-6">
                    You are about to visit a third-party site not owned or controlled by Clearpath Lending. We are not
                    responsible for its content, security, or accessibility.
                </p>

                {/* FULL destination link BEFORE the buttons */}
                {pendingHref && (
                    <div className="mt-4">
                        <div className="text-xs font-medium text-gray-500">Destination</div>
                        <a
                            href={pendingHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-1 block text-sm underline underline-offset-2 decoration-1 text-blue-600 hover:text-blue-700 hover:decoration-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 break-all"
                            title={pendingHref}
                        >
                            {pendingHref}
                        </a>
                    </div>
                )}

                <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                    <button
                        type="button"
                        onClick={cancel}
                        className="inline-flex items-center justify-center rounded-xl border px-4 py-2 text-sm font-medium hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={proceed}
                        className="inline-flex items-center justify-center rounded-xl bg-[#0A66C2] px-4 py-2 text-sm font-semibold text-white hover:opacity-95"
                    >
                        Continue
                    </button>
                </div>
            </div>
        </div>
    ) : null;

    return (
        <>
            <a
                href={href}
                onClick={handleClick}
                className={className}
                aria-label={ariaLabel}
                target={target} // keep this
                rel={target === "_blank" ? "noopener noreferrer" : rel}
            >
                {children}
            </a>

            {mounted && modal ? createPortal(modal, document.body) : null}
        </>
    );
}
